import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useMediaQuery } from 'react-responsive';
import Draggable from 'react-draggable';
import Fab from '@mui/material/Fab';
import RateReviewIcon from '@mui/icons-material/RateReview';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const EXCLUDED_PATHS = ['/login', '/register', '/about'];
const STORAGE_KEY = 'feedbackWidgetPos';

const FeedbackWidget = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const nodeRef = useRef(null);
  const didDragRef = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  const savedPos = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  const [dragPos] = useState(savedPos || { x: 0, y: 0 });

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState(false);

  if (!isAuthenticated || EXCLUDED_PATHS.includes(location.pathname)) return null;

  const handleDragStart = (_, data) => {
    dragStartPos.current = { x: data.x, y: data.y };
    didDragRef.current = false;
  };

  const handleDragStop = (_, data) => {
    const dx = Math.abs(data.x - dragStartPos.current.x);
    const dy = Math.abs(data.y - dragStartPos.current.y);
    const wasDrag = dx > 5 || dy > 5;
    didDragRef.current = wasDrag;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: data.x, y: data.y }));
    if (!wasDrag) setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCategory('general');
    setMessage('');
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await agent.Feedback.create({ category, message: message.trim() });
      handleClose();
      setSnackbar(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Draggable
        nodeRef={nodeRef}
        defaultPosition={dragPos}
        onStart={handleDragStart}
        onStop={handleDragStop}
        bounds="body"
      >
        <Fab
          ref={nodeRef}
          size="small"
          aria-label="feedback"
          sx={{
            position: 'fixed',
            bottom: isMobile ? 96 : 48,
            right: 16,
            zIndex: 1200,
            backgroundColor: DEFAULT_THEME_COLOR,
            color: 'white',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            '&:hover': { backgroundColor: DEFAULT_THEME_COLOR, opacity: 0.85 },
          }}
        >
          <RateReviewIcon />
        </Fab>
      </Draggable>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Send Feedback</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <FormControl fullWidth size="small">
            <InputLabel id="feedback-category-label">Category</InputLabel>
            <Select
              labelId="feedback-category-label"
              value={category}
              label="Category"
              onChange={e => setCategory(e.target.value)}
            >
              <MenuItem value="bug">Bug Report</MenuItem>
              <MenuItem value="suggestion">Suggestion</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Message"
            multiline
            rows={4}
            fullWidth
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, 500))}
            helperText={`${message.length}/500`}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!message.trim() || submitting}
            sx={{
              backgroundColor: DEFAULT_THEME_COLOR,
              '&:hover': { backgroundColor: DEFAULT_THEME_COLOR, opacity: 0.85 },
            }}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(false)}
        message="Feedback submitted — thank you!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default FeedbackWidget;
